
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import { ApplicantAssessment } from "@/store/modules/recruiter/interfaces";
import { redo_assessment_request } from "@/store/apis/recruiter";

export default Vue.extend({
  name: "CandidateAiAssessments",
  data() {
    return {
      loading: false,
      assessments: [] as ApplicantAssessment[],
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0
    };
  },
  computed: {
    totalPages(): number {
      // Use totalPages from API response if available, otherwise calculate
      if (this.applicantAssessments?.totalPages) {
        return this.applicantAssessments.totalPages;
      }
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    ...mapGetters("auth", {
      user: "GET_USER_DETAILS"
    }),
    ...mapGetters("recruiter", {
      applicantAssessments: "APPLICANT_ASSESSMENTS"
    })
  },
  async created() {
    await this.loadAssessments();
  },
  methods: {
    ...mapActions("recruiter", {
      fetchApplicantAssessments: "FETCH_APPLICANT_ASSESSMENTS"
    }),
    async loadAssessments() {
      this.loading = true;
      try {
        // Get the current user's applicant ID
        const applicantId = this.user?.id;
        if (!applicantId) {
          throw new Error("User ID not found");
        }

        // Fetch assessments for the current applicant with pagination
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage
        };

        const success = await this.fetchApplicantAssessments({
          applicant_id: applicantId,
          params: params
        });
        if (success) {
          // Handle paginated response structure
          if (this.applicantAssessments?.data) {
            this.assessments = this.applicantAssessments.data;
            this.totalItems = this.applicantAssessments.total || 0;
            console.log("Paginated response:", {
              assessments: this.assessments.length,
              total: this.totalItems,
              currentPage: this.currentPage,
              totalPages: this.totalPages
            });
          } else {
            // Handle non-paginated response (backward compatibility)
            this.assessments = this.applicantAssessments || [];
            this.totalItems = this.assessments.length;
            console.log("Non-paginated response:", {
              assessments: this.assessments.length,
              total: this.totalItems
            });
          }
        }
      } catch (error) {
        console.error("Failed to load assessments:", error);
        this.$store.commit("ROOT_ERROR", error, { root: true });
      } finally {
        this.loading = false;
      }
    },
    async onPageChange(page: number) {
      this.currentPage = page;
      await this.loadAssessments();
    },
    getDifficultyColor(difficulty: string): string {
      switch (difficulty) {
        case "beginner":
          return "green";
        case "intermediate":
          return "orange";
        case "advanced":
          return "red";
        default:
          return "grey";
      }
    },
    startAssessment(assessment: ApplicantAssessment) {
      if (this.isCompleted(assessment)) return;
      this.$router.push({
        name: "candidate-ai-assessment",
        params: { id: assessment.assessment.id.toString() }
      });
    },
    async handlePrimaryAction(assessment: ApplicantAssessment) {
      console.log(assessment, "assessment");
      if (this.isRedo(assessment)) return;
      if (this.isCompleted(assessment)) return;
      if (this.isCancelled(assessment)) {
        await this.requestRedo(assessment);
        return;
      }

      this.startAssessment(assessment);
    },
    isCompleted(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "COMPLETED" || status === "RESULT_PROCESSING";
    },
    isCancelled(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "CANCELLED";
    },
    isRedo(assessment: any): boolean {
      const status = this.extractStatus(assessment);
      return status === "REDO";
    },
    extractStatus(assessment: any): string | undefined {
      const directStatus = assessment?.status;
      const resultStatus = assessment?.result?.status;
      const firstResultStatus = Array.isArray(assessment?.results)
        ? assessment.results[0]?.status
        : undefined;
      return directStatus || resultStatus || firstResultStatus;
    },
    async requestRedo(_assessment: ApplicantAssessment) {
      const response = await redo_assessment_request(_assessment.assessment.id);
      if (response) {
        await this.loadAssessments();
        this.$store.commit(
          "ROOT_NOTIFICATION",
          this.$t("assessments.redo_notification").toString(),
          { root: true }
        );
      }
    },
    formatDate(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    }
  }
});
